import { useState, useEffect } from "react";
import { Form, Typography } from "antd";
import { useLocation } from "react-router-dom";
import "./processes.css";
import secureLocalStorage from "react-secure-storage";
import CustomLoader from "../../common/customLoader";
import TopContainerHeader from "../../../components/molecules/TopComponentHeader";

import TableContent from "../../../components/atoms/TableContent";
import AddProcess from "./addProcess";
import media from "../../../media";
import { useDispatch } from "react-redux";
import { fetchProcessesThunk } from "../../../store/thunks/processesThunk";
import { useSelector } from "react-redux";

const Processes = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = location.pathname.replace("/", "");
  const [tableForm] = Form.useForm();

  const { list, pageInfo, count, loading } = useSelector(
    (state) => state.qmsProcesses
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: Number(process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE || 50),
  });
  const shouldShowPagination = count > pagination.pageSize;
  const tablePagination = shouldShowPagination
    ? {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: count,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        locale: { items_per_page: "" },
        onChange: (page, pageSize) => {
          setPagination({ current: page, pageSize });
        },
      }
    : false;

  const slsRole = secureLocalStorage.getItem("role");  

  //States
  const [showLoader, setShowLoader] = useState(true);  
  const [departments, setDepartments] = useState([]);
  const [statuses, setStatuses] = useState([
    { label: "ACTIVE", value: "ACTIVE" },
    { label: "INACTIVE", value: "INACTIVE" },
  ]);  

  const [editingKey, setEditingKey] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  const openAddModal = () => {
    setEditRecord(null);
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  // Fetch processes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setShowLoader(true);
        const hasSearchedValue = secureLocalStorage.getItem("hasSearchedValue")
          ? "yes"
          : "";
        const searchKeyword = secureLocalStorage.getItem("searchKeyword") || "";

        const res = await dispatch(
          fetchProcessesThunk({
            searchIn: currentPath,
            callActionFrom: currentPath,
            hasSearchedValue,
            searchKeyword,
            route: currentPath,
            pagination: {
              current: pagination.current,
              page: pagination.current,
              pageSize: pagination.pageSize,
            },
          })
        ).unwrap();
      } catch (err) {
        console.error("Error fetching processes:", err);
      } finally {
        setShowLoader(false);
      }
    };

    fetchData();
  }, [currentPath, pagination.current, pagination.pageSize, dispatch]);
  
  const additionalColumns = [];
  const columns = [
    {
      title: () => {
        return <div className="tableTitle"> #</div>;
      },
      dataIndex: "id",

      render: (text) => <span className="rowstyle no-wrap">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentName.svg} alt="icon" />
            Name
          </div>
        );
      },
      dataIndex: "name",
      editable: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, row) => (
        <span className="rowstyle bfont no-wrap">{text}</span>
      ),
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentIcon.svg} alt="icon" />
            Department
          </div>
        );
      },
      dataIndex: "department",
      editable: true,
      render: (text) => <span className="rowstyle no-wrap">{text}</span>,
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentStatus.svg} alt="icon" />
            Status
          </div>
        );
      },
      dataIndex: "status",
      editable: true,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text, row) => (
        <span
          className={`cusStatus ${
            text?.toLowerCase() === "active" ? "cusActive" : "cusInActive"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: () => {
        return (
          <div className="tableTitle">
            <img src={media.departmentDate.svg} alt="icon" />
            Created At
          </div>
        );
      },
      dataIndex: "createdAt",
      render: (text) => <span className="rowstyle no-wrap">{text}</span>,
    },
  ];

  (slsRole === process.env.REACT_APP_AROLE ||
    slsRole === process.env.REACT_APP_QC) &&
    additionalColumns.push(
      {
        title: () => {
          return (
            <div className="tableTitle">
              <img src={media.departmentDate.svg} alt="icon" />
              Updated At
            </div>
          );
        },
        dataIndex: "updatedAt",
        render: (text) => <span className="rowstyle ">{text || "-"}</span>,
      },
      {
        title: () => {
          return (
            <div className="tableTitle">
              <img src={media.departmentAction.svg} alt="icon" />
              Action
            </div>
          );
        },
        dataIndex: "action",
        render: (_, record) => {
          return (
            <Typography.Link
              disabled={editingKey !== ""}
              onClick={() => openEditModal(record)}
              className="tableBtn bfont"
            >
              <i className="fa fa-pencil-square-o" aria-hidden="true"></i> Edit
            </Typography.Link>
          );
        },
      }
    );
  columns.push(...additionalColumns);
  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          editing: isEditing(record) ? true : undefined,
          cellconfig: {
            inputType: "text",
            dataIndex: col.dataIndex,
            title: typeof col.title === "string" ? col.title : undefined,
            departments,
            statuses,
          },
        }),
      };
    });

  const isEditing = (record) => record.key === editingKey;  

  return (
    <div className="all-content">
      <CustomLoader showLoader={showLoader} />

      <TopContainerHeader
        sectionTitle="Processes"
        sectionDescription={
          showLoader ? "Loading..." : `${list?.length}  Found`
        }
        hasSectionButton={true}
        sectionButtons={[
          {
            isVisible: true,
            itemType: "add",
            itemName: "Add",
            onClick: openAddModal,
          },
        ]}
        hasFilter={false}
        buttonTitle="Add New"
        FormComponent={AddProcess}
        formTitle={editRecord ? "Edit Process" : "Add Process"}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        formProps={{
          editData: editRecord,
          statuses: statuses,
          onSuccess: () => {
            dispatch(
              fetchProcessesThunk({
                searchIn: currentPath,
                callActionFrom: currentPath,
                hasSearchedValue: secureLocalStorage.getItem("hasSearchedValue")
                  ? "yes"
                  : "",
                searchKeyword:
                  secureLocalStorage.getItem("searchKeyword") || "",
                pagination: {
                  current: pagination.current,
                  page: pagination.current,
                  pageSize: pagination.pageSize,
                },
              })
            );
            setModalOpen(false);
          },
        }}
      />

      <div className="all-table-holder">
        <Form form={tableForm} component={false}>
          <TableContent
            rowClassName="editable-row"
            bordered={false}
            columns={mergedColumns}
            dataSource={list}
            pagination={tablePagination}
          />
        </Form>
      </div>
    </div>
  );
};
export default Processes;
